import React, { useEffect, useState } from "react";
import {
  X, History, Loader2, Workflow, User, Briefcase, Clock, Stethoscope,
  ShoppingCart, FileText, MessageSquare, ListChecks, Gauge,
} from "lucide-react";
import { diagnosticSessionsApi } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const sourceLabel = (s) =>
  `${s.filename || "source"}${s.chunkIndex != null ? ` · Section ${s.chunkIndex + 1}` : ""}`;

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5">
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      {children}
    </div>
  );
}

function DetailPane({ record }) {
  if (!record) {
    return (
      <div className="flex-1 flex items-center justify-center text-[13px] text-slate-400" data-testid="diag-history-empty-detail">
        Select a completed diagnostic to view its full record.
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto p-5" data-testid="diag-history-detail">
      <div className="text-[16px] font-bold text-slate-900">{record.flowchartName || "Diagnostic"}</div>
      <div className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
        <span>Flowchart v{record.flowchartVersion ?? 1}</span>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(record.completedAt)}</span>
        {record.technicianEmail && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {record.technicianEmail}</span>}
        {record.jobId && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" /> {record.jobId}</span>}
      </div>

      {record.finalDiagnosis && (
        <Section icon={Stethoscope} title="Final Diagnosis">
          <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[13px] text-slate-800">
            <div className="font-semibold">{record.finalDiagnosis.label}</div>
            {record.finalDiagnosis.detail && <div className="text-slate-600 mt-1">{record.finalDiagnosis.detail}</div>}
          </div>
        </Section>
      )}

      <Section icon={ListChecks} title={`Completed Steps (${(record.completedNodePath || []).length})`}>
        <div className="text-[12px] text-slate-500 font-mono break-all">{(record.completedNodePath || []).join(" \u2192 ")}</div>
      </Section>

      {(record.answers || []).length > 0 && (
        <Section icon={MessageSquare} title="Technician Answers">
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
            {record.answers.map((a, i) => (
              <div key={i} className="px-3 py-2 text-[12.5px] flex items-start justify-between gap-3">
                <span className="text-slate-600">{a.question}</span>
                <span className="font-semibold text-slate-900 shrink-0">{a.answer}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(record.measurements || []).length > 0 && (
        <Section icon={Gauge} title="Measurements Recorded">
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
            {record.measurements.map((a, i) => (
              <div key={i} className="px-3 py-2 text-[12.5px] flex items-start justify-between gap-3">
                <span className="text-slate-600">{a.question}</span>
                <span className="font-semibold text-slate-900 shrink-0">{a.answer}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(record.recommendedProducts || []).length > 0 && (
        <Section icon={ShoppingCart} title="Recommended Products">
          <div className="space-y-1.5">
            {record.recommendedProducts.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-1.5 text-[12.5px]">
                <span className="text-slate-800 font-medium">{p.name}</span>
                <span className="font-bold text-emerald-700">${Number(p.unitPrice || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(record.aiQA || []).length > 0 && (
        <Section icon={MessageSquare} title="Ask Guru — AI Q&A">
          <div className="space-y-2">
            {record.aiQA.map((m, i) => (
              <div key={i} className={`rounded-lg px-3 py-2 text-[12.5px] ${m.role === "user" ? "bg-slate-100 text-slate-700" : "bg-amber-50 border border-amber-200 text-slate-800"}`}>
                <div className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">{m.role === "user" ? "Technician" : "Guru"}</div>
                {m.text}
                {(m.sources || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {m.sources.map((s, si) => (
                      <span key={si} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                        <FileText className="h-2.5 w-2.5" /> {sourceLabel(s)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {(record.kbSourcesCited || []).length > 0 && (
        <Section icon={FileText} title="Knowledge Base Sources Cited">
          <div className="flex flex-wrap gap-1.5">
            {record.kbSourcesCited.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-100">
                <FileText className="h-3 w-3" /> {sourceLabel(s)}
              </span>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export default function DiagnosticHistoryModal({ open, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    diagnosticSessionsApi
      .historyList()
      .then((docs) => {
        setRecords(docs || []);
        setSelectedId((docs || [])[0]?.id || null);
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;
  const selected = records.find((r) => r.id === selectedId) || null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="diagnostic-history-modal">
      <div className="w-full max-w-4xl h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <History className="h-4.5 w-4.5 text-slate-700" />
            <div className="text-[15px] font-bold text-slate-900">Diagnostic History</div>
          </div>
          <button onClick={onClose} data-testid="diag-history-close" className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="w-[300px] border-r border-slate-100 overflow-y-auto shrink-0" data-testid="diag-history-list">
            {loading ? (
              <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : records.length === 0 ? (
              <div className="p-6 text-[12.5px] text-slate-400 text-center">No completed diagnostics yet.</div>
            ) : (
              records.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  data-testid={`diag-history-row-${r.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                    selectedId === r.id ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="text-[12.5px] font-semibold text-slate-800 flex items-center gap-1.5 truncate">
                    <Workflow className="h-3 w-3 text-blue-500 shrink-0" /> {r.flowchartName || "Diagnostic"}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(r.completedAt)}</div>
                  {r.finalDiagnosis?.label && (
                    <div className="text-[11px] text-violet-600 mt-0.5 truncate">{r.finalDiagnosis.label}</div>
                  )}
                </button>
              ))
            )}
          </div>
          <DetailPane record={selected} />
        </div>
      </div>
    </div>
  );
}
